import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { Check, Info, LoaderCircle, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Hotel } from "@/types/hotel";
import { HotelSubscriptionFeature } from "@/types/hotel-subscription";
import {
  SubscriptionPlan,
  SubscriptionPlanFeature,
} from "@/types/subscription-plan";
import { getCanonicalFeatureTitle } from "@/utils/accessControl";
import {
  getPlanDisplayFeatures,
  STANDARD_CORE_PLAN_DISPLAY_FEATURES,
} from "@/utils/subscriptionFeatureDisplay";
import {
  useAttachHotelSubscriptionPlan,
  useDetachHotelSubscriptionPlan,
  useHotelSubscription,
  useSubscriptionPaymentUrl,
  useTenantSubscriptionPlans,
  useToggleHotelSubscriptionFeature,
} from "@/components/pages/hotels/hooks/useHotelSubscriptions";

interface HotelSubscriptionPlansDialogProps {
  hotel: Hotel | null;
  canManage?: boolean;
  onClose: () => void;
}

const PAYMENT_POLLING_STORAGE_KEY =
  "hotel-subscription-payment-processing-hotel-id";
const PAYMENT_POLLING_STARTED_AT_STORAGE_KEY =
  "hotel-subscription-payment-processing-started-at";

function getBillingSubscriptionsReturnUrl(): string {
  const url = new URL("/billing-subscriptions", window.location.origin);
  return url.toString();
}

interface NormalizedPlanFeature {
  key: string;
  title?: string;
  type?: string;
  value?: string;
  limit?: unknown;
}

interface PlanFeatureItem {
  key: string;
  label: string;
  category: "core" | "paid";
  subscriptionFeature?: HotelSubscriptionFeature;
}

interface HotelSubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  isCurrent: boolean;
  hotelSubscriptionStatus?: string;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  nextPaymentDate?: string | null;
  assigningCurrent: boolean;
  isPending: boolean;
  isPaymentUrlPending: boolean;
  isDetaching: boolean;
  billingQuantity?: number;
  canManage: boolean;
  onSelectPlan: (plan: SubscriptionPlan) => void;
  onPayNow: () => void;
  onRemovePlan: () => void;
  renderPlanFeatures: (
    plan: SubscriptionPlan,
    isCurrent: boolean,
  ) => React.ReactNode;
}

type PlanCardActionState = ReturnType<typeof getPlanCardActionState>;

interface HotelSubscriptionPlanActionsProps {
  canManage: boolean;
  actionState: PlanCardActionState;
  assigningCurrent: boolean;
  paymentRequired: boolean;
  isPaymentUrlPending: boolean;
  isCurrent: boolean;
  isPending: boolean;
  isDetaching: boolean;
  onPrimaryAction: () => void;
  onRemovePlan: () => void;
}

function normalizePlanFeature(
  feature: SubscriptionPlanFeature,
): NormalizedPlanFeature {
  return {
    key:
      feature.key?.trim() || feature.feature?.key?.trim() || feature.featureId,
    title: feature.title ?? feature.feature?.title,
    type: feature.type ?? feature.feature?.type,
    value: feature.value,
    limit: feature.feature?.limit ?? feature.limit,
  };
}

function descriptionToText(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return "";
}

function formatMoney(price: number | string, currency: string): string {
  const raw =
    typeof price === "number" ? price : Number.parseFloat(String(price));
  const amount = Number.isFinite(raw) ? raw : 0;
  const code = currency.trim() === "" ? "EUR" : currency.trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = Number.parseFloat(value.trim());
    return Number.isFinite(normalized) ? normalized : null;
  }

  return null;
}

function getPlanRoomsLimit(plan: SubscriptionPlan): number | null {
  const roomsLimitFeature = plan.features
    .map(normalizePlanFeature)
    .find((feature) => feature.key === "rooms_limit");

  if (!roomsLimitFeature) {
    return null;
  }

  const parsedRoomsLimit = parseFiniteNumber(
    roomsLimitFeature.value ?? roomsLimitFeature.limit,
  );

  if (parsedRoomsLimit === null || parsedRoomsLimit <= 0) {
    return null;
  }

  return parsedRoomsLimit;
}

function getFullSubscriptionPrice(plan: SubscriptionPlan): number | null {
  const perRoomPrice = parseFiniteNumber(plan.price);
  const roomsLimit = getPlanRoomsLimit(plan);

  if (perRoomPrice === null || roomsLimit === null) {
    return null;
  }

  return perRoomPrice * roomsLimit;
}

function getRoomBasedSubscriptionPrice(
  plan: SubscriptionPlan,
  roomsNumber: number | null,
): number | null {
  const perRoomPrice = parseFiniteNumber(plan.price);

  if (perRoomPrice === null || roomsNumber === null) {
    return null;
  }

  return perRoomPrice * roomsNumber;
}

function parseRoomsNumber(value: string): number | null {
  const parsed = Number.parseInt(value, 10);

  if (
    !Number.isFinite(parsed) ||
    parsed < 1 ||
    String(parsed) !== value.trim()
  ) {
    return null;
  }

  return parsed;
}

function getDefaultRoomsNumber(hotel: Hotel | null): number {
  return Math.max(1, hotel?.rooms?.length ?? 1);
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return format(date, "dd.MM.yyyy");
}

function formatSubscriptionPeriod(
  currentPeriodStart: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
): string {
  if (!currentPeriodStart || !currentPeriodEnd) {
    return "-";
  }

  return `${formatShortDate(currentPeriodStart)} - ${formatShortDate(currentPeriodEnd)}`;
}

function formatFeatureValue(feature: NormalizedPlanFeature): string | null {
  const rawValue = feature.value ?? feature.limit;

  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  const value = String(rawValue).trim();
  return value === "" || value === "true" ? null : value;
}

function formatHotelFeatureValue(
  feature: HotelSubscriptionFeature,
): string | null {
  const rawValue = feature.value;

  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  if (typeof rawValue === "boolean") {
    return null;
  }

  const value = String(rawValue).trim();
  return value === "" || value === "true" ? null : value;
}

function isCurrentPlan(plan: SubscriptionPlan, currentPlanId?: string) {
  return currentPlanId !== undefined && plan.id === currentPlanId;
}

function requiresPayment(status?: string): boolean {
  return (
    status === "INCOMPLETE" || status === "PAST_DUE" || status === "UNPAID"
  );
}

function uniquePaidFeatures(
  features: SubscriptionPlanFeature[],
): NormalizedPlanFeature[] {
  return Array.from(
    new Map(
      features
        .map(normalizePlanFeature)
        .map((feature) => [feature.key, feature]),
    ).values(),
  );
}

const CORE_DISPLAY_FEATURE_BY_KEY = new Map<
  string,
  (typeof STANDARD_CORE_PLAN_DISPLAY_FEATURES)[number]
>(STANDARD_CORE_PLAN_DISPLAY_FEATURES.map((feature) => [feature.key, feature]));

function getSubscriptionCoreDisplayFeature(feature: HotelSubscriptionFeature) {
  if (feature.key === "rooms_limit") {
    return CORE_DISPLAY_FEATURE_BY_KEY.get("rooms");
  }

  return CORE_DISPLAY_FEATURE_BY_KEY.get(feature.key);
}

function isRapidPlanTitle(planTitle?: string | null): boolean {
  return planTitle?.trim().toLowerCase() === "rapid";
}

function getPlanCardActionState(params: {
  planTitle: string;
  isCurrent: boolean;
  paymentRequired: boolean;
  isPending: boolean;
  isPaymentUrlPending: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const {
    planTitle,
    isCurrent,
    paymentRequired,
    isPending,
    isPaymentUrlPending,
    t,
  } = params;

  if (paymentRequired) {
    return {
      label: t("hotels.subscriptions.payNow"),
      variant: "new_default" as const,
      disabled: isPending || isPaymentUrlPending,
    };
  }

  if (isCurrent) {
    return {
      label: t("hotels.subscriptions.currentPlan"),
      variant: "outline" as const,
      disabled: true,
    };
  }

  return {
    label: t("hotels.subscriptions.selectPlan", {
      planTitle,
    }),
    variant: "new_default" as const,
    disabled: isPending || isPaymentUrlPending,
  };
}

const HotelSubscriptionPlanActions: React.FC<
  HotelSubscriptionPlanActionsProps
> = ({
  canManage,
  actionState,
  assigningCurrent,
  paymentRequired,
  isPaymentUrlPending,
  isCurrent,
  isPending,
  isDetaching,
  onPrimaryAction,
  onRemovePlan,
}) => {
  const { t } = useTranslation();

  if (!canManage) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        variant={actionState.variant}
        className="h-10 w-full max-w-sm self-center"
        disabled={actionState.disabled}
        onClick={onPrimaryAction}
      >
        {assigningCurrent || (paymentRequired && isPaymentUrlPending) ? (
          <LoaderCircle className="size-4 animate-spin" />
        ) : null}
        {actionState.label}
      </Button>

      {isCurrent ? (
        <Button
          type="button"
          variant="destructive"
          className="h-10 w-full max-w-sm self-center"
          disabled={isPending || isPaymentUrlPending || isDetaching}
          onClick={onRemovePlan}
        >
          {isDetaching ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : null}
          {t("hotels.subscriptions.removePlan")}
        </Button>
      ) : null}
    </>
  );
};

const HotelSubscriptionPlanCard: React.FC<HotelSubscriptionPlanCardProps> = ({
  plan,
  isCurrent,
  hotelSubscriptionStatus,
  currentPeriodStart,
  currentPeriodEnd,
  nextPaymentDate,
  assigningCurrent,
  isPending,
  isPaymentUrlPending,
  isDetaching,
  billingQuantity,
  canManage,
  onSelectPlan,
  onPayNow,
  onRemovePlan,
  renderPlanFeatures,
}) => {
  const { t } = useTranslation();
  const description = descriptionToText(plan.description);
  const paymentRequired = isCurrent && requiresPayment(hotelSubscriptionStatus);
  const price = formatMoney(plan.price, plan.currency);
  const fullSubscriptionPrice =
    isCurrent && billingQuantity !== undefined
      ? getRoomBasedSubscriptionPrice(plan, billingQuantity)
      : getFullSubscriptionPrice(plan);
  const actionState = getPlanCardActionState({
    planTitle: plan.title,
    isCurrent,
    paymentRequired,
    isPending,
    isPaymentUrlPending,
    t,
  });
  const handlePrimaryAction = () => {
    if (paymentRequired) {
      onPayNow();
      return;
    }

    onSelectPlan(plan);
  };

  return (
    <article
      className={[
        "bg-background flex h-full max-h-[calc(100vh-220px)] flex-col gap-5 overflow-y-auto overscroll-contain rounded-xl border p-8 shadow-sm transition-colors",
        isCurrent
          ? "border-royal-glow-3 shadow-[0_0_0_1px_rgba(110,86,255,0.2)]"
          : "border-border",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-foreground text-xl font-semibold">{plan.title}</h3>
        {isCurrent ? (
          <Badge variant="new_default">
            {t("hotels.subscriptions.current")}
          </Badge>
        ) : null}
      </div>

      <p className="text-muted-foreground min-h-12 text-sm leading-6">
        {description || t("hotels.subscriptions.noDescription")}
      </p>

      <div className="flex items-end gap-2 pt-1">
        <span className="text-foreground text-4xl font-semibold">{price}</span>
        <span className="text-muted-foreground pb-1 text-sm">
          {t("hotels.subscriptions.perRoom")}
        </span>
      </div>
      {fullSubscriptionPrice !== null ? (
        <p className="text-muted-foreground text-sm">
          {t("hotels.subscriptions.fullPrice", {
            price: formatMoney(fullSubscriptionPrice, plan.currency),
          })}
        </p>
      ) : null}

      {isCurrent ? (
        <dl className="border-border/60 bg-background grid grid-cols-1 gap-3 rounded-lg border p-3 text-xs sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground">
              {t("hotels.subscriptions.period")}
            </dt>
            <dd className="text-foreground font-medium">
              {formatSubscriptionPeriod(currentPeriodStart, currentPeriodEnd)}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground">
              {t("hotels.subscriptions.nextPayment")}
            </dt>
            <dd className="text-foreground font-medium">
              {formatShortDate(nextPaymentDate)}
            </dd>
          </div>
        </dl>
      ) : null}

      <HotelSubscriptionPlanActions
        canManage={canManage}
        actionState={actionState}
        assigningCurrent={assigningCurrent}
        paymentRequired={paymentRequired}
        isPaymentUrlPending={isPaymentUrlPending}
        isCurrent={isCurrent}
        isPending={isPending}
        isDetaching={isDetaching}
        onPrimaryAction={handlePrimaryAction}
        onRemovePlan={onRemovePlan}
      />

      <div className="flex flex-1 flex-col gap-4">
        <p className="text-foreground text-sm font-semibold">
          {isCurrent
            ? t("hotels.subscriptions.connectedModules")
            : t("hotels.subscriptions.included")}
        </p>
        {renderPlanFeatures(plan, isCurrent)}
      </div>
    </article>
  );
};

export const HotelSubscriptionPlansDialog: React.FC<
  HotelSubscriptionPlansDialogProps
> = ({ hotel, canManage = true, onClose }) => {
  const { t } = useTranslation();
  const open = hotel !== null;
  const hotelId = hotel?.id ?? null;
  const { data: plans = [], isLoading: plansLoading } =
    useTenantSubscriptionPlans(open);
  const {
    data: hotelSubscription,
    isLoading: subscriptionLoading,
    refetch: refetchHotelSubscription,
  } = useHotelSubscription(hotelId, open);
  const { mutateAsync: attachPlan, isPending } =
    useAttachHotelSubscriptionPlan();
  const { mutateAsync: detachPlan, isPending: isDetaching } =
    useDetachHotelSubscriptionPlan();
  const { mutateAsync: getPaymentUrl, isPending: isPaymentUrlPending } =
    useSubscriptionPaymentUrl();
  const { mutateAsync: toggleHotelFeature, isPending: isTogglingFeature } =
    useToggleHotelSubscriptionFeature();
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [togglingFeatureKey, setTogglingFeatureKey] = useState<string | null>(
    null,
  );
  const [confirmingPlan, setConfirmingPlan] = useState<SubscriptionPlan | null>(
    null,
  );
  const [roomsNumberInput, setRoomsNumberInput] = useState("");
  const [confirmingDetach, setConfirmingDetach] = useState(false);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const currentPlanId = hotelSubscription?.planId;
  const sortedPlans = useMemo(
    () =>
      [...plans].sort((left, right) => {
        if (isCurrentPlan(left, currentPlanId)) {
          return -1;
        }
        if (isCurrentPlan(right, currentPlanId)) {
          return 1;
        }
        return Number(left.price) - Number(right.price);
      }),
    [currentPlanId, plans],
  );
  const selectedRoomsNumber = parseRoomsNumber(roomsNumberInput);
  const confirmingFinalPrice = confirmingPlan
    ? getRoomBasedSubscriptionPrice(confirmingPlan, selectedRoomsNumber)
    : null;

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setConfirmingPlan(plan);
    setRoomsNumberInput(String(getDefaultRoomsNumber(hotel)));
  };

  const closeConfirmingPlan = () => {
    setConfirmingPlan(null);
    setRoomsNumberInput("");
  };

  const handleAssignPlan = async () => {
    if (!canManage) {
      return;
    }

    if (hotelId === null) {
      return;
    }

    if (confirmingPlan === null) {
      return;
    }

    if (selectedRoomsNumber === null) {
      toast.error(t("hotels.subscriptions.confirm.roomsNumberError"));
      return;
    }

    try {
      setPendingPlanId(confirmingPlan.id);
      await attachPlan({
        hotelId,
        planId: confirmingPlan.id,
        roomsNumber: selectedRoomsNumber,
      });
      await refetchHotelSubscription();
      toast.success(
        t("hotels.subscriptions.assignSuccess", {
          planTitle: confirmingPlan.title,
        }),
      );
      closeConfirmingPlan();
    } catch {
      toast.error(t("hotels.subscriptions.assignError"));
    } finally {
      setPendingPlanId(null);
    }
  };

  const handlePayNow = async () => {
    if (!canManage) {
      return;
    }

    const subscriptionId = hotelSubscription?.tenantSubscriptionId;

    if (!subscriptionId || hotelId === null) {
      toast.error(t("hotels.subscriptions.paymentUrlError"));
      return;
    }

    try {
      const response = await getPaymentUrl({
        subscriptionId,
        returnUrl: getBillingSubscriptionsReturnUrl(),
      });

      if (!response.url) {
        await refetchHotelSubscription();
        toast.error(t("hotels.subscriptions.paymentUrlUnavailable"));
        return;
      }

      window.sessionStorage.setItem(PAYMENT_POLLING_STORAGE_KEY, hotelId);
      window.sessionStorage.setItem(
        PAYMENT_POLLING_STARTED_AT_STORAGE_KEY,
        String(Date.now()),
      );
      window.location.assign(response.url);
    } catch {
      toast.error(t("hotels.subscriptions.paymentUrlError"));
    }
  };

  const handleDetachPlan = async () => {
    if (!canManage) {
      return;
    }

    if (hotelId === null) {
      return;
    }

    try {
      await detachPlan(hotelId);
      await refetchHotelSubscription();
      toast.success(t("hotels.subscriptions.detachSuccess"));
      setConfirmingDetach(false);
    } catch {
      toast.error(t("hotels.subscriptions.detachError"));
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setIsRefreshingStatus(true);
      await refetchHotelSubscription();
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const handleToggleFeature = async (featureKey: string, enabled: boolean) => {
    if (!canManage) {
      return;
    }

    if (hotelId === null) {
      return;
    }

    const featureTitle = getCanonicalFeatureTitle(featureKey);

    try {
      setTogglingFeatureKey(featureKey);
      await toggleHotelFeature({ hotelId, featureKey, enabled });
      await refetchHotelSubscription();
      toast.success(
        t(
          enabled
            ? "hotels.subscriptions.featureEnabled"
            : "hotels.subscriptions.featureDisabled",
          { feature: featureTitle },
        ),
      );
    } catch {
      toast.error(t("hotels.subscriptions.featureToggleError"));
    } finally {
      setTogglingFeatureKey(null);
    }
  };

  const renderPlanFeatures = (plan: SubscriptionPlan, isCurrent: boolean) => {
    const currentSubscriptionFeatures = hotelSubscription?.features ?? [];
    const featureItems: PlanFeatureItem[] =
      isCurrent && currentSubscriptionFeatures.length > 0
        ? Array.from(
            new Map(
              currentSubscriptionFeatures.map((subscriptionFeature) => {
                const coreFeature =
                  getSubscriptionCoreDisplayFeature(subscriptionFeature);
                const displayKey = coreFeature?.key ?? subscriptionFeature.key;
                const title = coreFeature
                  ? t(coreFeature.titleKey)
                  : getCanonicalFeatureTitle(subscriptionFeature.key);
                const value = formatHotelFeatureValue(subscriptionFeature);

                return [
                  displayKey,
                  {
                    key: displayKey,
                    label: value ? `${title} (${value})` : title,
                    category: coreFeature
                      ? ("core" as const)
                      : ("paid" as const),
                    subscriptionFeature,
                  },
                ];
              }),
            ).values(),
          )
        : getPlanDisplayFeatures(
            uniquePaidFeatures(plan.features),
            plan.enableCoreFeatures === true && !isRapidPlanTitle(plan.title),
          ).map((feature) => {
            if ("titleKey" in feature) {
              return {
                key: feature.key,
                label: t(feature.titleKey),
                category: "core" as const,
              };
            }

            const title = getCanonicalFeatureTitle(feature.key, feature.title);
            const value = formatFeatureValue(feature);

            return {
              key: feature.key,
              label: value ? `${title} (${value})` : title,
              category: "paid" as const,
            };
          });

    const hasFeatures = featureItems.length > 0;

    if (!hasFeatures) {
      return (
        <p className="text-muted-foreground text-sm">
          {t("hotels.subscriptions.noFeatures")}
        </p>
      );
    }

    return (
      <ul className="flex flex-col gap-2.5">
        {featureItems.map((feature) => {
          const canToggleFeature =
            canManage &&
            isCurrent &&
            feature.subscriptionFeature?.type === "BOOLEAN";
          const isFeatureEnabled = feature.subscriptionFeature?.enabled ?? true;
          const isSwitchPending =
            isTogglingFeature && togglingFeatureKey === feature.key;
          const isSwitchDisabled =
            isTogglingFeature ||
            isPending ||
            isPaymentUrlPending ||
            isDetaching;

          return (
            <li
              key={feature.key}
              className="text-muted-foreground grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-3 text-sm"
            >
              {feature.category === "core" ? (
                <Minus className="text-foreground size-4" aria-hidden />
              ) : (
                <Check className="text-foreground size-4" aria-hidden />
              )}
              <span className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 truncate">{feature.label}</span>
                {feature.subscriptionFeature?.overriddenByHotel ? (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {t("hotels.subscriptions.disabledForHotel")}
                  </Badge>
                ) : null}
              </span>
              {canToggleFeature ? (
                <span className="flex items-center justify-end gap-2">
                  {isSwitchPending ? (
                    <LoaderCircle className="text-muted-foreground size-4 animate-spin" />
                  ) : null}
                  <Switch
                    checked={isFeatureEnabled}
                    disabled={isSwitchDisabled}
                    aria-label={t("hotels.subscriptions.toggleFeature", {
                      feature: feature.label,
                    })}
                    onCheckedChange={(checked) =>
                      void handleToggleFeature(feature.key, checked)
                    }
                  />
                </span>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground/70 hover:text-foreground focus-visible:ring-ring rounded-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      aria-label={t("hotels.subscriptions.featureInfoLabel", {
                        feature: feature.label,
                      })}
                    >
                      <Info className="size-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs">
                    {t(
                      feature.category === "core"
                        ? "hotels.subscriptions.coreFeatureInfo"
                        : "hotels.subscriptions.includedFeatureInfo",
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="!top-0 !left-0 !h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 overflow-x-hidden overflow-y-auto rounded-none border-0 px-10 pt-8 pb-10 shadow-none [&>*]:min-w-0">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-3xl font-semibold tracking-normal">
            {t("hotels.subscriptions.title")}
          </DialogTitle>
          <DialogDescription>
            {t("hotels.subscriptions.description", {
              hotelName: hotel?.name ?? "—",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end">
          {hotelSubscription ? (
            <Button
              type="button"
              variant="outline"
              disabled={isRefreshingStatus || subscriptionLoading}
              onClick={() => void handleRefreshStatus()}
            >
              {isRefreshingStatus ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              {t("hotels.subscriptions.refreshStatus")}
            </Button>
          ) : null}
        </div>

        {plansLoading || subscriptionLoading ? (
          <div className="text-muted-foreground flex min-h-48 items-center justify-center">
            <LoaderCircle className="size-5 animate-spin" />
          </div>
        ) : null}

        {!plansLoading && !subscriptionLoading && sortedPlans.length === 0 ? (
          <div className="border-border text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
            {t("hotels.subscriptions.empty")}
          </div>
        ) : null}

        {!plansLoading && !subscriptionLoading && sortedPlans.length > 0 ? (
          <Carousel
            opts={{
              align: "start",
              containScroll: "trimSnaps",
            }}
            className="min-w-0 overflow-hidden px-0 sm:px-14"
          >
            <CarouselContent className="-ml-5">
              {sortedPlans.map((plan) => {
                const current = isCurrentPlan(plan, currentPlanId);
                const assigningCurrent = isPending && pendingPlanId === plan.id;
                const displayPlan =
                  current && hotelSubscription?.plan
                    ? {
                        ...plan,
                        title: hotelSubscription.plan.title,
                        price: hotelSubscription.plan.price,
                        currency: hotelSubscription.plan.currency,
                      }
                    : plan;

                return (
                  <CarouselItem
                    key={plan.id}
                    className="basis-full pl-5 sm:basis-[430px] xl:basis-[460px]"
                  >
                    <HotelSubscriptionPlanCard
                      plan={displayPlan}
                      isCurrent={current}
                      hotelSubscriptionStatus={hotelSubscription?.status}
                      billingQuantity={
                        current ? hotelSubscription?.billingQuantity : undefined
                      }
                      currentPeriodStart={
                        current ? hotelSubscription?.currentPeriodStart : null
                      }
                      currentPeriodEnd={
                        current ? hotelSubscription?.currentPeriodEnd : null
                      }
                      nextPaymentDate={
                        current ? hotelSubscription?.nextPaymentDate : null
                      }
                      assigningCurrent={assigningCurrent}
                      isPending={isPending}
                      isPaymentUrlPending={isPaymentUrlPending}
                      isDetaching={isDetaching}
                      canManage={canManage}
                      onSelectPlan={handleSelectPlan}
                      onPayNow={() => void handlePayNow()}
                      onRemovePlan={() => setConfirmingDetach(true)}
                      renderPlanFeatures={renderPlanFeatures}
                    />
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="top-1/2 left-2 z-10 hidden sm:inline-flex" />
            <CarouselNext className="top-1/2 right-2 z-10 hidden sm:inline-flex" />
          </Carousel>
        ) : null}
      </DialogContent>
      <Dialog
        open={confirmingPlan !== null}
        onOpenChange={(nextOpen) => !nextOpen && closeConfirmingPlan()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("hotels.subscriptions.confirm.title")}</DialogTitle>
            <DialogDescription>
              {t("hotels.subscriptions.confirm.description", {
                hotelName: hotel?.name ?? "—",
                planTitle: confirmingPlan?.title ?? "—",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              label={t("hotels.subscriptions.confirm.roomsNumber")}
              description={t(
                "hotels.subscriptions.confirm.roomsNumberDescription",
              )}
              value={roomsNumberInput}
              aria-invalid={selectedRoomsNumber === null}
              onChange={(event) => setRoomsNumberInput(event.target.value)}
            />
            <div className="border-border bg-muted/30 grid grid-cols-1 gap-3 rounded-lg border p-4 text-sm sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">
                  {t("hotels.subscriptions.confirm.perRoomPrice")}
                </span>
                <span className="text-foreground font-medium">
                  {confirmingPlan
                    ? formatMoney(confirmingPlan.price, confirmingPlan.currency)
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">
                  {t("hotels.subscriptions.confirm.rooms")}
                </span>
                <span className="text-foreground font-medium">
                  {selectedRoomsNumber ?? "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">
                  {t("hotels.subscriptions.confirm.finalPrice")}
                </span>
                <span className="text-foreground font-semibold">
                  {confirmingFinalPrice !== null && confirmingPlan
                    ? formatMoney(confirmingFinalPrice, confirmingPlan.currency)
                    : "-"}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isPending}>
                {t("hotels.subscriptions.confirm.cancel")}
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="new_default"
              disabled={isPending || selectedRoomsNumber === null}
              onClick={() => void handleAssignPlan()}
            >
              {isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              {t("hotels.subscriptions.confirm.assign")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmingDetach}
        onOpenChange={(nextOpen) => !nextOpen && setConfirmingDetach(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("hotels.subscriptions.detachConfirm.title")}
            </DialogTitle>
            <DialogDescription>
              {t("hotels.subscriptions.detachConfirm.description", {
                hotelName: hotel?.name ?? "—",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isDetaching}>
                {t("hotels.subscriptions.detachConfirm.cancel")}
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              disabled={isDetaching}
              onClick={() => void handleDetachPlan()}
            >
              {isDetaching ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              {t("hotels.subscriptions.detachConfirm.remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
